// Persian → English digit conversion + phone normalization

import type { IUser } from "@/types/auth";

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

export function toEnglishDigits(input: string): string {
  return input.replace(/[۰-۹٠-٩]/g, (d) => {
    const p = PERSIAN_DIGITS.indexOf(d);
    if (p > -1) return String(p);
    const a = ARABIC_DIGITS.indexOf(d);
    if (a > -1) return String(a);
    return d;
  });
}

export function toPersianDigits(input: string | number): string {
  return String(input).replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)]);
}

/**
 * Normalize Iranian mobile number.
 * Accepts: 09xxxxxxxxx | +98xxxxxxxxxx → returns 09xxxxxxxxx
 * Rejects: 0098..., other formats → returns ""
 */
export function normalizeMobile(raw: string): string {
  let v = toEnglishDigits(raw || "")
    .replace(/[\s\u200c\u200f\u200e-]/g, "")
    .trim();

  if (v.startsWith("0098")) return ""; // explicit reject per spec
  if (v.startsWith("+98")) v = "0" + v.slice(3);
  else if (v.startsWith("98") && v.length === 12) v = "0" + v.slice(2);

  // If user just typed digits, keep as-is
  return v;
}

export function isValidMobile(v: string): boolean {
  return /^09\d{9}$/.test(v);
}

export function maskMobile(v: string): string {
  if (!isValidMobile(v)) return v;
  return `${v.slice(0, 4)}***${v.slice(7)}`;
}

export function uuidv4(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Password policy: ≥8, upper, lower, digit, special
export function validatePassword(pw: string) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
}

export function isStrongPassword(pw: string) {
  const v = validatePassword(pw);
  return v.length && v.upper && v.lower && v.digit && v.special;
}

export function getUserDisplayName(user: IUser | null): string {
  if (!user) return "";
  const full = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return full || user.name?.trim() || "";
}

export function getUserFirstName(user: IUser | null): string {
  if (!user) return "";
  return user.first_name?.trim() || getUserDisplayName(user).split(" ")[0] || "";
}

export function getUserInitial(user: IUser | null): string {
  return getUserDisplayName(user).charAt(0);
}

export function getUserContact(user: IUser | null): string {
  if (!user) return "";
  return user.email || user.mobile;
}
